import { type RefObject, useLayoutEffect, useState } from 'react';

/**
 * A height that takes `ref`'s element from wherever it starts down to the
 * bottom of the window, less `bottomInset` (to clear something fixed there),
 * and never under `min`. For a Spreadsheet whose rows should scroll rather
 * than the page: measure an element wrapping the grid and pass the result as
 * its `maxHeight`. Undefined until measured.
 */
export function useFillHeight(
  ref: RefObject<HTMLElement | null>,
  { bottomInset = 0, min = 0 }: { bottomInset?: number; min?: number } = {}
): number | undefined {
  const [height, setHeight] = useState<number>();
  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      // The element's place on the page, not in the window: the height is
      // what fits when the page is scrolled to the top, and it stays the same
      // as the page scrolls. Measuring from the window instead would grow the
      // grid by however far the page is scrolled.
      const top = el.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(min, window.innerHeight - top - bottomInset));
    };
    measure();
    window.addEventListener('resize', measure);
    // Content above the element can grow or shrink and move it while the
    // window stays the same size; the page's size changes with it.
    const observer = new ResizeObserver(measure);
    observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      observer.disconnect();
    };
  }, [ref, bottomInset, min]);
  return height;
}
